import { motion, useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';

import { T } from '../textes-interface.js';

/**
 * La boîte de dialogue narrative du jeu : situation de départ, événements,
 * retours sur une solution. Le texte vient toujours du contenu ; ce composant ne
 * fait que le mettre en scène.
 */
export function MessageDuMaire({
  titre,
  children,
  onContinuer,
  onFermer,
}: {
  titre: string;
  children: ReactNode;
  onContinuer?: () => void;
  onFermer?: () => void;
}) {
  const animationsReduites = useReducedMotion();

  return (
    <motion.section
      initial={animationsReduites ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      aria-label={T.maire.rôle}
      className="rounded-2xl border-2 border-sky-200 bg-white p-4 shadow-sm"
    >
      <header className="flex items-start gap-3">
        <span aria-hidden="true" className="text-3xl leading-none">
          🏛️
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold uppercase tracking-wide text-sky-700">
            {T.maire.rôle}
          </p>
          <h2 className="mt-1 text-xl font-bold text-slate-900">{titre}</h2>
        </div>

        {onFermer && (
          <button
            type="button"
            onClick={onFermer}
            aria-label={T.maire.fermer}
            className="min-h-touch min-w-touch rounded-lg text-slate-500 hover:bg-slate-100"
          >
            <span aria-hidden="true">✕</span>
          </button>
        )}
      </header>

      <div className="mt-3 space-y-2 text-slate-800">{children}</div>

      {onContinuer && (
        <footer className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onContinuer}
            className="min-h-touch rounded-xl bg-sky-600 px-5 font-semibold text-white hover:bg-sky-700"
          >
            {T.maire.continuer}
          </button>
        </footer>
      )}
    </motion.section>
  );
}
